import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { parseISO, addDays } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { iso } from "@/lib/booking";
import { Card } from "./BookingsTab";

const SRC = { manual: ["Bloqueo manual", "bg-[#2563EB] text-white"], airbnb: ["Airbnb", "bg-[#FF5A5F] text-white"] };

export const CalendarTab = ({ prop }) => {
  const unitColor = prop.color || "#16A34A";
  const [blocks, setBlocks] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [range, setRange] = useState();
  const [note, setNote] = useState("");
  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get(`/admin/properties/${prop.id}/blocks`), api.get(`/admin/bookings?property_id=${prop.id}`)]);
    setBlocks(Array.isArray(a.data) ? a.data : []);
    setBookings((Array.isArray(b.data) ? b.data : []).filter((x) => x.status !== "cancelled"));
  }, [prop.id]);
  useEffect(() => { load(); }, [load]);

  const mods = useMemo(() => {
    const toR = (s, e) => ({ from: parseISO(s), to: addDays(parseISO(e), -1) });
    return {
      manual: blocks.filter((b) => b.source === "manual").map((b) => toR(b.start, b.end)),
      airbnb: blocks.filter((b) => b.source === "airbnb").map((b) => toR(b.start, b.end)),
      booked: bookings.map((b) => toR(b.check_in, b.check_out)),
    };
  }, [blocks, bookings]);

  const add = async () => {
    if (!range?.from) return;
    const end = range.to ? addDays(range.to, 1) : addDays(range.from, 1);
    try {
      await api.post(`/admin/properties/${prop.id}/blocks`, { start: iso(range.from), end: iso(end), note });
      toast.success("Fechas bloqueadas");
      setRange(undefined);
      setNote("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    await api.delete(`/admin/blocks/${id}`);
    load();
  };

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-12" data-testid="admin-calendar">
      <Card className="lg:col-span-7">
        <p className="font-display text-2xl">Bloquear fechas</p>
        <p className="mt-1 text-sm text-[#4A3525]">Selecciona las noches que no estarán disponibles.</p>
        <div className="mt-4 overflow-x-auto">
          <Calendar
            mode="range" numberOfMonths={2} selected={range} onSelect={setRange} locale={es}
            modifiers={mods}
            modifiersClassNames={{ manual: "!bg-[#2563EB] !text-white", airbnb: "!bg-[#FF5A5F] !text-white", booked: "unit-booked !text-white" }}
            modifiersStyles={{ booked: { backgroundColor: unitColor } }}
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-3 text-xs">
          <span className="flex items-center gap-1"><i className="h-3 w-3 rounded" style={{ backgroundColor: unitColor }} />Reserva directa ({prop.name})</span>
          <span className="flex items-center gap-1"><i className="h-3 w-3 rounded bg-[#FF5A5F]" />Airbnb</span>
          <span className="flex items-center gap-1"><i className="h-3 w-3 rounded bg-[#2563EB]" />Bloqueo manual</span>
        </div>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <Input data-testid="block-note-input" placeholder="Nota (opcional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <button data-testid="add-block-button" disabled={!range?.from} onClick={add} className="rounded-lg bg-[#2C2016] px-5 py-2 text-sm font-semibold text-white disabled:opacity-40">Bloquear</button>
        </div>
      </Card>
      <Card className="space-y-3 lg:col-span-5">
        <p className="font-display text-2xl">Fechas no disponibles</p>
        {blocks.length === 0 && <p className="text-sm text-[#4A3525]">Sin bloqueos.</p>}
        {blocks.map((b) => (
          <div key={b.id} className="flex items-center justify-between gap-3 border-b border-[#2C2016]/10 pb-3 text-sm">
            <div>
              <p className="font-medium">{b.start} → {b.end}</p>
              <p className="text-xs text-[#4A3525]">{b.note}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-[11px] ${SRC[b.source]?.[1]}`}>{SRC[b.source]?.[0]}</span>
              {b.source === "manual" && <button data-testid={`delete-block-${b.id}`} onClick={() => del(b.id)} className="rounded p-1 hover:bg-[#2C2016]/5"><Trash2 className="h-4 w-4" /></button>}
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
};
